'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ContentFiles, PriceDisplay } from '@/components/partials';
import AddToCartAction from '@/components/partials/card/add-to-cart-action';
import { formatNumber } from '@/lib/utils';
import { useLoginDialogStore } from '@/store';
import { ProductType, ProductOemType } from '@/types/product';
type PropsType = {
  product: ProductType;
  backendLocale: string;
  productTitle: string;
  isAuthenticated: boolean;
  categoryLinks: Array<{
    id: string;
    label: string;
    href: string;
  }>;
  // When set, the page is scoped to a single OEM: the OEM No / brand / model rows show that OEM.
  selectedOem?: ProductOemType;
};
const has = (value: unknown): boolean => value !== undefined && value !== null;
const joinUnique = (values: Array<string | null | undefined>) => Array.from(new Set(values.filter((value): value is string => !!value && value.trim().length > 0))).join(', ');
const ProductDetailHero = t0 => {
  const $ = _c(106);
  const {
    product,
    backendLocale,
    productTitle,
    isAuthenticated,
    categoryLinks,
    selectedOem
  } = t0;
  const t = useTranslations();
  const {
    open: openLoginDialog
  } = useLoginDialogStore();
  let brandValue;
  let oemValue;
  let t1;
  if ($[0] !== product.oemNumbers || $[1] !== selectedOem) {
    const brands = product.oemNumbers?.map(_temp) ?? [];
    const models = product.oemNumbers?.map(_temp2) ?? [];
    oemValue = selectedOem ? selectedOem.oem : joinUnique(product.oemNumbers?.map(_temp3) ?? []);
    brandValue = selectedOem ? selectedOem.model.brand.name : joinUnique(brands);
    t1 = selectedOem ? selectedOem.model.name : joinUnique(models);
    $[0] = product.oemNumbers;
    $[1] = selectedOem;
    $[2] = brandValue;
    $[3] = oemValue;
    $[4] = t1;
  } else {
    brandValue = $[2];
    oemValue = $[3];
    t1 = $[4];
  }
  const modelValue = t1;
  let t2;
  if ($[5] !== t) {
    t2 = t("product_code");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== product.code || $[8] !== t2) {
    t3 = {
      label: t2,
      value: product.code
    };
    $[7] = product.code;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== oemValue || $[11] !== t) {
    t4 = oemValue ? [{
      label: t("oem_no"),
      value: oemValue
    }] : [];
    $[10] = oemValue;
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== brandValue || $[14] !== selectedOem || $[15] !== t) {
    t5 = brandValue ? [{
      label: selectedOem ? t("brand") : t("brands"),
      value: brandValue
    }] : [];
    $[13] = brandValue;
    $[14] = selectedOem;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== modelValue || $[18] !== selectedOem || $[19] !== t) {
    t6 = modelValue ? [{
      label: selectedOem ? t("model") : t("models"),
      value: modelValue
    }] : [];
    $[17] = modelValue;
    $[18] = selectedOem;
    $[19] = t;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== backendLocale || $[22] !== product.weight || $[23] !== t) {
    t7 = has(product.weight) ? [{
      label: t("weight"),
      value: `${formatNumber(product.weight as number, backendLocale)} kg`
    }] : [];
    $[21] = backendLocale;
    $[22] = product.weight;
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== backendLocale || $[26] !== product.boxDimensionalWeight || $[27] !== t) {
    t8 = has(product.boxDimensionalWeight) ? [{
      label: t("box_dimensional_weight"),
      value: formatNumber(product.boxDimensionalWeight as number, backendLocale)
    }] : [];
    $[25] = backendLocale;
    $[26] = product.boxDimensionalWeight;
    $[27] = t;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== backendLocale || $[30] !== product.boxHeight || $[31] !== product.boxLength || $[32] !== product.boxWidth || $[33] !== t) {
    t9 = has(product.boxLength) && has(product.boxWidth) && has(product.boxHeight) ? [{
      label: t("box_dimensions"),
      value: `${formatNumber(product.boxLength as number, backendLocale)} × ${formatNumber(product.boxWidth as number, backendLocale)} × ${formatNumber(product.boxHeight as number, backendLocale)} cm`
    }] : [];
    $[29] = backendLocale;
    $[30] = product.boxHeight;
    $[31] = product.boxLength;
    $[32] = product.boxWidth;
    $[33] = t;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== backendLocale || $[36] !== product.unitsPerBox || $[37] !== t) {
    t10 = has(product.unitsPerBox) ? [{
      label: t("units_per_box"),
      value: formatNumber(product.unitsPerBox as number, backendLocale)
    }] : [];
    $[35] = backendLocale;
    $[36] = product.unitsPerBox;
    $[37] = t;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
    t11 = [t3, ...t4, ...t5, ...t6, ...t7, ...t8, ...t9, ...t10];
    $[39] = t10;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  const summary = t11;
  let t12;
  if ($[48] !== productTitle) {
    t12 = <h1 className="text-3xl font-extrabold tracking-tight text-balance md:text-2xl">{productTitle}</h1>;
    $[48] = productTitle;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Separator />;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== categoryLinks) {
    t14 = categoryLinks.length > 0 && <div className="flex flex-wrap gap-2">{categoryLinks.map(_temp4)}</div>;
    $[51] = categoryLinks;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t12 || $[54] !== t14) {
    t15 = <div className="space-y-2">{t12}{t13}{t14}</div>;
    $[53] = t12;
    $[54] = t14;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== summary) {
    t16 = <dl className="overflow-hidden rounded-md border bg-white">{summary.map((row, index) => <div key={row.label} className={`group grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)] ${index !== summary.length - 1 ? "border-b" : ""}`}><dt className="group-hover:bg-brand bg-gray-100 px-3 py-2 text-sm! font-semibold text-gray-700 transition-colors group-hover:text-black">{row.label}</dt><dd className="group-hover:bg-brand/5 px-3 py-2 text-sm! transition-colors md:text-base">{row.value}</dd></div>)}</dl>;
    $[56] = summary;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== selectedOem || $[59] !== t) {
    t17 = selectedOem?.model.files && selectedOem.model.files.length > 0 && <div className="space-y-2"><p className="text-sm font-semibold">{t("downloadable_files")}</p><ContentFiles files={selectedOem.model.files} size="sm" /></div>;
    $[58] = selectedOem;
    $[59] = t;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== t15 || $[62] !== t16 || $[63] !== t17) {
    t18 = <div className="flex flex-col gap-4">{t15}{t16}{t17}</div>;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t) {
    t19 = t("price");
    $[65] = t;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== t19) {
    t20 = <p className="text-muted-foreground text-right text-xs font-medium uppercase">{t19}</p>;
    $[67] = t19;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== backendLocale || $[70] !== isAuthenticated || $[71] !== openLoginDialog || $[72] !== product.price?.currencyCode || $[73] !== product.price?.discountRate || $[74] !== product.price?.gross || $[75] !== product.price?.originalGross || $[76] !== product.priceIncludesTax || $[77] !== t) {
    t21 = <div className="mt-2 text-right">{isAuthenticated ? <PriceDisplay gross={product.price?.gross} originalGross={product.price?.originalGross} discountRate={product.price?.discountRate} currencyCode={product.price?.currencyCode} locale={backendLocale} size="lg" showTaxBreakdown={true} priceIncludesTax={product.priceIncludesTax} /> : <button onClick={openLoginDialog} className="text-muted-foreground hover:text-brand border-muted-foreground hover:border-brand cursor-pointer rounded border px-2 py-0.5 text-xs transition-colors">{t("login_to_see_price")}</button>}</div>;
    $[69] = backendLocale;
    $[70] = isAuthenticated;
    $[71] = openLoginDialog;
    $[72] = product.price?.currencyCode;
    $[73] = product.price?.discountRate;
    $[74] = product.price?.gross;
    $[75] = product.price?.originalGross;
    $[76] = product.priceIncludesTax;
    $[77] = t;
    $[78] = t21;
  } else {
    t21 = $[78];
  }
  let t22;
  if ($[79] !== t20 || $[80] !== t21) {
    t22 = <div className="rounded-md border p-4">{t20}{t21}</div>;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[82] !== t) {
    t23 = t("stock_status");
    $[82] = t;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== t23) {
    t24 = <p className="text-muted-foreground text-xs font-medium uppercase">{t23}</p>;
    $[84] = t23;
    $[85] = t24;
  } else {
    t24 = $[85];
  }
  const t25 = `text-sm font-semibold ${product.stock > 0 ? "text-success" : "text-destructive"}`;
  let t26;
  if ($[86] !== product.stock || $[87] !== t) {
    t26 = product.stock > 0 ? t("in_stock") : t("out_of_stock");
    $[86] = product.stock;
    $[87] = t;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  let t27;
  if ($[89] !== t25 || $[90] !== t26) {
    t27 = <p className={t25}>{t26}</p>;
    $[89] = t25;
    $[90] = t26;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  let t28;
  if ($[92] !== t24 || $[93] !== t27) {
    t28 = <div className="flex flex-col items-end justify-center gap-1 rounded-md border bg-white p-4">{t24}{t27}</div>;
    $[92] = t24;
    $[93] = t27;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  let t29;
  if ($[95] !== t22 || $[96] !== t28) {
    t29 = <div className="grid grid-cols-[1fr_auto] gap-3">{t22}{t28}</div>;
    $[95] = t22;
    $[96] = t28;
    $[97] = t29;
  } else {
    t29 = $[97];
  }
  let t30;
  if ($[98] !== product) {
    t30 = <AddToCartAction product={product} className="w-full justify-end" />;
    $[98] = product;
    $[99] = t30;
  } else {
    t30 = $[99];
  }
  let t31;
  if ($[100] !== t29 || $[101] !== t30) {
    t31 = <div className="flex flex-col gap-3">{t29}{t30}</div>;
    $[100] = t29;
    $[101] = t30;
    $[102] = t31;
  } else {
    t31 = $[102];
  }
  let t32;
  if ($[103] !== t18 || $[104] !== t31) {
    t32 = <div className="flex flex-col gap-6 lg:h-150 lg:justify-between">{t18}{t31}</div>;
    $[103] = t18;
    $[104] = t31;
    $[105] = t32;
  } else {
    t32 = $[105];
  }
  return t32;
};
export default ProductDetailHero;
function _temp(item) {
  return item.model.brand.name;
}
function _temp2(item_0) {
  return item_0.model.name;
}
function _temp3(oem) {
  return oem.oem;
}
function _temp4(category) {
  return <Badge key={category.id} asChild={true} className="hover:bg-brand! rounded-md bg-neutral-600 px-3 py-1 text-sm font-semibold text-white transition-colors duration-300"><Link href={category.href}>{category.label}</Link></Badge>;
}